import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FieldtimeDialog from './FieldtimeDialog';

/**
 * ConfirmDialog — confirmation for actions that CANNOT be undone
 * (UI Design Standards §4.9). Reversible actions use Undo via useNotify.
 *
 * If onConfirm returns a promise, the confirm button shows a spinner and both
 * buttons are disabled until it settles. Resolve → onClose(). Reject → stays
 * open; the caller shows the error with useNotify.
 *
 * Props:
 *   open, onClose, title     as FieldtimeDialog; title states the action
 *   message       {node}     one sentence of consequence
 *   confirmLabel  {string}   e.g. "Delete package" (default "Confirm")
 *   cancelLabel   {string}   default "Cancel"
 *   confirmIcon   {element}  optional start icon
 *   destructive   {bool}     confirm button in error color
 *   onConfirm     {func}     may return a promise
 *   overBoard     {bool}     see FieldtimeDialog
 *   children      optional extra content (e.g. a checkbox)
 */
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmIcon,
  destructive = false,
  overBoard = false,
  children,
}) => {
  const { ft } = useTheme().palette;
  const [pending, setPending] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleClose = (...args) => {
    if (!pending) onClose?.(...args);
  };

  const handleConfirm = async () => {
    let result;
    try {
      result = onConfirm?.();
    } catch {
      return; // stays open; caller reports the error
    }
    if (result && typeof result.then === 'function') {
      setPending(true);
      try {
        await result;
      } catch {
        if (mounted.current) setPending(false);
        return;
      }
      if (mounted.current) setPending(false);
    }
    onClose?.();
  };

  return (
    <FieldtimeDialog
      open={open}
      onClose={handleClose}
      title={title}
      size="sm"
      overBoard={overBoard}
      actions={
        <>
          <Button
            onClick={handleClose}
            disabled={pending}
            sx={{ color: ft.neutrals.textBody }}
          >
            {cancelLabel}
          </Button>
          <Button
            variant="contained"
            color={destructive ? 'error' : 'primary'}
            onClick={handleConfirm}
            disabled={pending}
            startIcon={
              pending ? <CircularProgress size={16} color="inherit" /> : confirmIcon
            }
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {message && (
        <Typography sx={{ fontSize: 14, color: ft.neutrals.textBody }}>
          {message}
        </Typography>
      )}
      {children && <Box sx={{ mt: message ? 2 : 0 }}>{children}</Box>}
    </FieldtimeDialog>
  );
};

export default ConfirmDialog;
